import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { metricsService } from '../services/endpoints/metricsService';
import { useCoachView } from '../contexts/CoachViewContext';
import { DailyMetrics } from '../types';
import { parseLocalDate } from '../utils/dateUtils';
import CoachCommentThread from '../components/coach/CoachCommentThread';
import MetricsDetailDrawer from '../components/metrics/MetricsDetailDrawer';

const PAGE_SIZE = 25;

const Metrics: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const openMetricId = searchParams.get('open');
  const { viewAs, isCoachView } = useCoachView();
  const [metrics, setMetrics] = useState<DailyMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<DailyMetrics | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  // Date range inputs — defaults to last 30 days
  const [dateFrom, setDateFrom] = useState(() => format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [dateTo, setDateTo]     = useState(() => format(new Date(), 'yyyy-MM-dd'));

  // Applied range — only updated when "Apply" is clicked so the query doesn't
  // fire on every keystroke when the user is typing a date.
  const [appliedFrom, setAppliedFrom] = useState(dateFrom);
  const [appliedTo, setAppliedTo]     = useState(dateTo);

  const handleDelete = async (metricId: string) => {
    try {
      await metricsService.deleteMetrics(metricId);
      setConfirmDeleteId(null);
      await loadMetrics();
      setError(null);
    } catch (err) {
      setError('Failed to delete metrics');
      console.error(err);
    }
  };

  const loadMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const data = await metricsService.getMetrics(
        new Date(appliedFrom).toISOString(),
        new Date(appliedTo + 'T23:59:59').toISOString(),
        undefined,
        viewAs ?? undefined,
      );
      setMetrics(data);
      setCurrentPage(1);
      setError(null);
    } catch (err) {
      setError('Failed to load metrics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [appliedFrom, appliedTo, viewAs]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  // Auto-open the detail drawer (coach) or inline thread (athlete) from a
  // notification deep-link (?open=metricId)
  useEffect(() => {
    if (!openMetricId || metrics.length === 0 || !isCoachView) return;
    const match = metrics.find((m) => m.metricId === openMetricId);
    if (match) setSelectedMetric(match);
  }, [openMetricId, metrics, isCoachView]);

  const handleApplyDateRange = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
  };

  // Export — fetches ALL records regardless of the current date filter
  const exportToCSV = async () => {
    setIsExporting(true);
    setError(null);
    try {
      const allMetrics = await metricsService.getMetrics(
        undefined,
        undefined,
        undefined,
        viewAs ?? undefined,
      );

      if (allMetrics.length === 0) {
        setError('No check-in data to export.');
        return;
      }

      const headers = [
        'Date',
        'Weight (lbs)',
        'HRV',
        'Resting HR (bpm)',
        'Steps (Yesterday)',
        'BP Systolic (mmHg)',
        'BP Diastolic (mmHg)',
        'Glucose (mg/dL)',
        'Sleep Hours',
        'Sleep Quality',
        'Wellbeing Score',
        'Had BM',
        'BM Consistency',
        'PED Side Effects',
        'PED Side Effect Notes',
        'Notes',
      ];

      const rows = allMetrics.map((m: any) => [
        parseLocalDate(m.date).toLocaleDateString(),
        m.weight ?? '',
        m.overnightHrv ?? '',
        m.overnightRestingHeartRate ?? '',
        m.stepsYesterday ?? '',
        m.bloodPressureSystolic ?? '',
        m.bloodPressureDiastolic ?? '',
        m.glucoseLevel ?? '',
        m.sleepHours ?? '',
        m.sleepQuality ?? '',
        m.wellbeingScore ?? '',
        m.hadBowelMovement ? 'Yes' : (m.hadBowelMovement === false ? 'No' : ''),
        m.bowelMovementConsistency ?? '',
        m.pedSideEffects ? 'Yes' : (m.pedSideEffects === false ? 'No' : ''),
        m.pedSideEffectsNotes ? `"${String(m.pedSideEffectsNotes).replace(/"/g, '""')}"` : '',
        m.notes ? `"${String(m.notes).replace(/"/g, '""')}"` : '',
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map((row: any[]) => row.join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `check-ins_all_${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError('Failed to export data. Please try again.');
      console.error(err);
    } finally {
      setIsExporting(false);
    }
  };

  // ── Pagination ────────────────────────────────────────────────────────────
  const totalPages       = Math.max(1, Math.ceil(metrics.length / PAGE_SIZE));
  const paginatedMetrics = metrics.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-400">Loading check-ins...</div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="mb-6 flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gold-500 mb-2">Daily Check-ins</h1>
          <p className="text-gray-400">Tracking data for the selected date range</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {!isCoachView && (
            <button
              onClick={() => navigate('/metrics/entry')}
              className="bg-gold-500 hover:bg-gold-600 text-black font-bold py-2 px-6 rounded transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Log Today's Check-in
            </button>
          )}
          {/* Export All — fetches every record, not just the current date window */}
          <button
            onClick={exportToCSV}
            disabled={isExporting}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-2 px-6 rounded transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {isExporting ? 'Exporting…' : 'Export All'}
          </button>
        </div>
      </div>

      {/* ── Date Range Controls ──────────────────────────────────────────────── */}
      <div className="bg-dark-800 rounded-lg p-4 mb-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="bg-dark-700 border border-dark-600 text-white rounded px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="bg-dark-700 border border-dark-600 text-white rounded px-3 py-2 text-sm"
          />
        </div>
        <button
          onClick={handleApplyDateRange}
          className="bg-gold-500 hover:bg-gold-600 text-black font-bold py-2 px-5 rounded text-sm transition-colors"
        >
          Apply
        </button>
        <span className="text-sm text-gray-500 self-center">
          {metrics.length} {metrics.length === 1 ? 'entry' : 'entries'} found
        </span>
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {metrics.length === 0 ? (
        <div className="bg-dark-800 rounded-lg p-8 text-center">
          <p className="text-gray-400 mb-2">No check-in data for this date range</p>
          <p className="text-sm text-gray-500">Try a wider date range or start logging daily check-ins</p>
        </div>
      ) : (
        <div className="bg-dark-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-dark-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Weight</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Steps (Yesterday)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Sleep</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Well-being</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Notes</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {paginatedMetrics.map((metric) => (
                  <React.Fragment key={metric.metricId}>
                  <tr className="hover:bg-dark-700 transition-colors">
                    {/* Date */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-white font-medium">
                      {parseLocalDate(metric.date).toLocaleDateString()}
                    </td>
                    {/* Weight */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                      {metric.weight ? `${metric.weight} lbs` : '-'}
                    </td>
                    {/* Steps */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                      {metric.stepsYesterday != null ? metric.stepsYesterday.toLocaleString() : '-'}
                    </td>
                    {/* Sleep */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                      {metric.sleepHours ? (
                        <div>
                          <div>{metric.sleepHours} hrs</div>
                          {metric.sleepQuality && (
                            <div className="text-xs text-gray-400">Quality: {metric.sleepQuality}/10</div>
                          )}
                        </div>
                      ) : '-'}
                    </td>
                    {/* Well-being */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {metric.wellbeingScore ? (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          metric.wellbeingScore >= 8 ? 'bg-green-900 text-green-200' :
                          metric.wellbeingScore >= 6 ? 'bg-yellow-900 text-yellow-200' :
                          'bg-red-900 text-red-200'
                        }`}>
                          {metric.wellbeingScore}/10
                        </span>
                      ) : '-'}
                    </td>
                    {/* Notes + photo indicator */}
                    <td className="px-4 py-3 text-sm text-gray-400 max-w-xs">
                      <div className="flex items-center gap-2">
                        <span className="truncate">{metric.notes || '-'}</span>
                        {(metric.photoCount ?? 0) > 0 && (
                          <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-dark-700 text-gray-400 border border-dark-600">
                            📷 {metric.photoCount}
                          </span>
                        )}
                      </div>
                    </td>
                    {/* Actions */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {isCoachView ? (
                        <button
                          onClick={() => setSelectedMetric(metric)}
                          className="text-blue-400 hover:text-blue-300 font-medium"
                        >
                          View Details
                        </button>
                      ) : confirmDeleteId === metric.metricId ? (
                        /* Inline delete confirmation — no browser confirm() */
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">Delete this entry?</span>
                          <button
                            onClick={() => handleDelete(metric.metricId)}
                            className="text-xs px-2 py-1 bg-red-600 hover:bg-red-500 text-white font-semibold rounded transition-colors"
                          >
                            Yes, Delete
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            className="text-xs px-2 py-1 bg-dark-600 hover:bg-dark-500 border border-dark-500 text-gray-300 font-semibold rounded transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-3">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              const dateStr = metric.date.includes('T') ? metric.date.split('T')[0] : metric.date;
                              navigate(`/metrics/entry?date=${dateStr}`);
                            }}
                            className="text-gold-400 hover:text-gold-300 font-medium"
                          >
                            View/Edit
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(metric.metricId)}
                            className="text-red-400 hover:text-red-300 font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={99} className="px-4 pb-2">
                      <CoachCommentThread
                        contextType="DailyCheckIn"
                        contextId={metric.metricId}
                        label="check-in"
                        defaultExpanded={!isCoachView && openMetricId === metric.metricId}
                      />
                    </td>
                  </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Footer: count + pagination ─────────────────────────────────── */}
          <div className="px-4 py-3 bg-dark-700 border-t border-gray-700 flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-gray-400">
              {metrics.length > PAGE_SIZE
                ? `Showing ${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, metrics.length)} of ${metrics.length} entries`
                : `${metrics.length} ${metrics.length === 1 ? 'entry' : 'entries'}`}
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm rounded bg-dark-600 border border-dark-500 text-gray-300 hover:bg-dark-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ← Prev
                </button>
                <span className="text-sm text-gray-400">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm rounded bg-dark-600 border border-dark-500 text-gray-300 hover:bg-dark-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Coach detail drawer */}
      <MetricsDetailDrawer
        metric={selectedMetric}
        onClose={() => setSelectedMetric(null)}
      />
    </div>
  );
};

export default Metrics;
